import { useCallback, useMemo } from "react";
import { skipToken, useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { omitUndefined, toastManager } from "@salestrak/ui";
import {
  GetMyPreferencesDocument,
  GetUserSettingsDocument,
  type MyProfile,
  type Preferences,
  UpdatePreferencesDocument,
  type UpdatePreferencesInput,
  UpdateUserDocument,
} from "#app/lib/data";
import { useAuth } from "#app/lib/auth-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";
import { createEditSessionGate } from "#app/lib/analytics/edit-session";

// A slider or a color picker writes many times in one change; count the
// burst once per field set.
const preferenceSession = createEditSessionGate(3_000);

// Row aliases, so both carry the schema's own nullability: `name`,
// `username`, `email`, `defaultRoute`, `displayFullNames` and `fontSize` are
// ALL nullable, and callers must handle a missing value (R2, R4).
export type UserSettings = MyProfile;
export type PreferencesSettings = Preferences;

/**
 * The scalar preference fields — the same set the resolver dispatches through
 * its own scalar-key map.
 *
 * The list-valued fields (favorites, workflowOrder, railTiles,
 * homeSectionOrder) are deliberately out. They replace the whole list, and
 * their INPUT shape is not the shape the mutation RETURNS, so they cannot be
 * spread into an optimistic response the way a scalar can. Derived from the
 * generated input, so it cannot drift from the schema.
 */
export type ScalarPreferenceInput = Pick<
  UpdatePreferencesInput,
  | "autoAssignSelf"
  | "autoAssignTeam"
  | "compactRail"
  | "railCountDot"
  | "showEmptyProperties"
  | "defaultRoute"
  | "displayFullNames"
  | "firstDayOfWeek"
  | "timeFormat"
  | "fontSize"
  | "themeColor"
  | "themeMode"
  | "themeLightAccent"
  | "themeLightBackground"
  | "themeLightForeground"
  | "themeDarkAccent"
  | "themeDarkBackground"
  | "themeDarkForeground"
  | "themeLightContrast"
  | "themeDarkContrast"
  | "themeLightNeutralPrimary"
  | "themeDarkNeutralPrimary"
  | "themeName"
  | "themeLightName"
  | "themeDarkName"
  | "themeRadius"
  | "themeShadows"
  | "toastPosition"
  | "reduceMotion"
  | "pointerCursors"
>;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

// Write-only preference mutation, split out so consumers that only write (and
// read elsewhere) don't mount a cache-and-network settings observer per call site.
//
// EVERY field that changes together has to travel in ONE call. The optimistic
// response is built by spreading the CACHED preferences, and a batch of calls
// made in the same tick all read the same pre-change snapshot — so the last
// one's optimistic write reverts the fields the earlier ones had just set, and
// the UI snaps back until the server round-trips. Passing them together is also
// one mutation instead of N.
export function useUpdatePreferences() {
  const client = useApolloClient();
  const [updatePreferencesMutation] = useMutation(UpdatePreferencesDocument);

  return useCallback(
    // No success toast: a preference is a small choice whose control shows
    // its new state the instant the optimistic write lands — that IS the
    // feedback. Errors still toast. (The toast-position row raises its own
    // preview toast, because there the point is to show where toasts go.)
    // `defaultTeamIds` rides along with the scalars: a plain id list in both
    // directions, so it spreads into the optimistic response like one.
    async (fields: ScalarPreferenceInput & { defaultTeamIds?: string[] }) => {
      // Read cached preferences to build the optimistic response (instant updates).
      const cached = client.readQuery({ query: GetMyPreferencesDocument });
      const current = cached?.myPreferences;

      try {
        await updatePreferencesMutation({
          variables: { input: fields },
          ...(current && {
            optimisticResponse: {
              updatePreferences: {
                ...current,
                // An absent field must not overwrite the cached value with
                // `undefined` — under exactOptionalPropertyTypes the key has to
                // be gone, not present-and-undefined.
                ...omitUndefined(fields),
              },
            },
          }),
        });
        const keys = Object.keys(fields).sort();
        if (preferenceSession(keys.join(","))) {
          trackEvent({
            context: AnalyticsContext.Settings,
            event: SettingsEvent.Update,
            label: "preferences",
            data: { fields: keys },
          });
        }
      } catch (err) {
        toastMutationError(err, "Failed to update preference");
      }
    },
    [client, updatePreferencesMutation]
  );
}

// Single-field convenience over the batch writer above. Safe on its own; two
// of these in one tick is the race the batch writer exists to avoid.
export function useUpdatePreference() {
  const updatePreferences = useUpdatePreferences();

  return useCallback(
    (
      fieldId: keyof ScalarPreferenceInput,
      value: string | boolean | number | null,
    ) => updatePreferences({ [fieldId]: value }),
    [updatePreferences]
  );
}

export function useUserSettings() {
  const { isAuthenticated } = useAuth().state;
  const { data, loading, error, refetch } = useQuery(
    GetUserSettingsDocument,
    isAuthenticated ? { fetchPolicy: "cache-and-network" } : skipToken,
  );

  const [updateUserMutation] = useMutation(UpdateUserDocument);

  const user = useMemo<UserSettings | null>(() => data?.me ?? null, [data]);

  const preferences = useMemo<PreferencesSettings | null>(
    () => data?.myPreferences ?? null,
    [data]
  );

  const updateUserField = useCallback(
    // `null` clears a nullable field (the job title); text fields save strings.
    async (fieldId: string, value: string | null) => {
      if (!user?.userId) return;
      try {
        const { data } = await updateUserMutation({
          variables: {
            id: user.userId,
            input: { [fieldId]: value },
          },
        });
        const result = data?.updateUser;
        // errorPolicy "all": a rejected mutation RESOLVES with no data (the
        // error link has already toasted the denial) — success may only be
        // announced when the server actually returned the updated user.
        if (!result) return;
        if ("code" in result) {
          toastManager.add({ title: result.message ?? "Failed to update profile", type: "error" });
          return;
        }
        // No success toast: saves arrive keystroke-debounced from the
        // auto-saving EditableField, so one would fire per typing pause.
        // The persisted value in the field is the feedback; errors still toast.
      } catch (err) {
        toastMutationError(err, "Failed to update profile");
      }
    },
    [user?.userId, updateUserMutation]
  );

  const updatePreference = useUpdatePreference();

  return {
    user,
    preferences,
    // Loading until the auth check answers: the query waits for sign-in.
    isLoading: loading || isAuthenticated === null,
    error,
    refetch,
    updateUserField,
    updatePreference,
  };
}
